import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import {
  CargoType,
  CarrierType,
  CarrierVisitDirection,
  RailVisitResponseDto,
  TruckVisitDto,
  VesselVisitDto,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { RailcarControlledAutocomplete } from '@planning/components/autocompletes/railcar-autocomplete/RailcarControlledAutocomplete'
import { YesNoChipBox } from '@planning/components/dangerous-goods/YesNoChipBox'
import IconTruck from '@planning/components/icon/IconTruck'
import { RailVisitAutoComplete } from '@planning/components/visit/RailVisitAutoComplete'
import { VesselVisitAutoComplete } from '@planning/components/visit/VesselVisitAutoComplete'
import RailTrackControlledAutocomplete from '@planning/pages/Order/components/RailTrackControlledAutocomplete'
import { GeneralCargoViewStore } from '@planning/stores/generalCargo/GeneralCargoViewStore'
import { useTranslate } from '@tolgee/react'
import { FieldBox, useMinimalsTheme } from '@tom-ui/ui'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useEffect, useState } from 'react'
import { Control, Controller, FieldError, useFormContext } from 'react-hook-form'
import { ICreateGeneralCargoOrderFormData } from '../CreateGeneralCargoOrderPageForm'

interface IProps {
  store: GeneralCargoViewStore
  isOrderFulfilled?: boolean
  order?: any // TODO Fix type
  visitData?: VesselVisitDto | TruckVisitDto | RailVisitResponseDto
  carrierVisitType?: CarrierType
  isTruckAppointment?: boolean
  disableCarrierVisitSelection?: boolean
  onValidationParamsChanged: (params: any) => void
}

const RenderDirectionExtraField: FC<{
  control: Control<ICreateGeneralCargoOrderFormData>
  carrierVisit: any
  error?: FieldError
  disabled?: boolean
  carrierType?: CarrierType
  onCarrierVisitChange?: (visit?: VesselVisitDto | RailVisitResponseDto) => void
}> = ({ control, error, carrierVisit, carrierType, disabled, onCarrierVisitChange }) => {
  const { t } = useTranslate()

  if (carrierType === CarrierType.Vessel || carrierType === CarrierType.Train) {
    return (
      <Controller
        control={control}
        name={'carrierVisit'}
        rules={{ required: true }}
        render={({ field: { onChange } }) => {
          const vesselVisitAutoComplete = (
            <VesselVisitAutoComplete
              selectedVesselVisit={carrierVisit}
              handleOnChange={visit => {
                onChange(visit)
                onCarrierVisitChange?.(visit)
              }}
              error={!!error}
              helperText={error ? t('fieldIsRequired', 'Field is required.') : ''}
              disabled={disabled}
              cargoType={CargoType.GeneralCargo}
            />
          )

          const railVisitAutoComplete = (
            <RailVisitAutoComplete
              selectedRailVisit={carrierVisit}
              handleOnChange={visit => {
                onChange(visit)
                onCarrierVisitChange?.(visit)
              }}
              error={!!error}
              helperText={error ? t('fieldIsRequired', 'Field is required.') : ''}
              disabled={disabled}
              cargoType={CargoType.GeneralCargo}
            />
          )

          switch (carrierType) {
            case CarrierType.Vessel:
              return vesselVisitAutoComplete
            case CarrierType.Train:
              return railVisitAutoComplete
            default:
              return <></>
          }
        }}
      />
    )
  }

  if (carrierType === CarrierType.Truck) {
    return (
      <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
        <IconTruck zoom={150} />
        <Typography variant='subtitle1' ml='.5rem'>
          {t('truck', 'Truck')}
        </Typography>
      </Box>
    )
  }
}

export const HandlingDirectionControl: FC<IProps> = observer(
  ({
    store,
    order,
    visitData,
    carrierVisitType,
    isTruckAppointment,
    isOrderFulfilled,
    disableCarrierVisitSelection,
    onValidationParamsChanged,
  }) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()

    const {
      control,
      getValues,
      formState: { errors },
      setValue,
    } = useFormContext<ICreateGeneralCargoOrderFormData>()

    const { railVisitItemStore, railTrackItemStore } = usePlanningStore()

    const [direction, setDirection] = useState<CarrierVisitDirection>(
      getValues('direction') ?? CarrierVisitDirection.Inbound,
    )

    const [carrierType, setCarrierType] = useState(carrierVisitType ?? order?.data.carrierVisitType)
    const [carrierVisit, setCarrierVisit] = useState<
      VesselVisitDto | RailVisitResponseDto | undefined
    >((visitData ?? order?.visit) as VesselVisitDto | RailVisitResponseDto)

    const getRailTracksByVisit = useCallback(
      (visitId?: number) => {
        const railTracks = railTrackItemStore.activeRailTracks

        if (visitId) {
          const railTrackIds = railVisitItemStore.getById(visitId)?.data.assignedRailTrackIds || []
          return railTracks.filter(x => railTrackIds.includes(x.id))
        }

        return railTracks
      },
      [railTrackItemStore, railVisitItemStore],
    )

    const railTracks = computed(() => getRailTracksByVisit(carrierVisit?.id)).get()

    useEffect(() => {
      if (!carrierVisit || carrierType !== CarrierType.Train) return

      const visit = carrierVisit as RailVisitResponseDto
      railVisitItemStore.fetchById(visit.id)
      railTrackItemStore.fetch()
    }, [carrierVisit, carrierType, railVisitItemStore, railTrackItemStore])

    const resetTransportFields = () => {
      setValue('carrierVisit', undefined)
      setValue('carrierVisitType', undefined)
      setValue('railTrackId', undefined)
      setValue('railcarId', undefined)
      setCarrierType(undefined)
      setCarrierVisit(undefined)
    }

    return (
      <Stack gap={2}>
        <FieldBox
          titleColor={store.selectedOrder || visitData ? theme.palette.text.disabled : undefined}
          label={t('handlingDirection', 'Handling direction')}
          rightRender={
            <Controller
              control={control}
              name={`direction`}
              render={({ field: { value, onChange } }) => (
                <Box>
                  <Chip
                    label={t('inbound', 'Inbound')}
                    color='primary'
                    sx={{ padding: '0 1rem', mr: '.75rem' }}
                    variant={value === CarrierVisitDirection.Inbound ? undefined : 'outlined'}
                    onClick={() => {
                      onChange(CarrierVisitDirection.Inbound)
                      setDirection(CarrierVisitDirection.Inbound)
                      resetTransportFields()
                      onValidationParamsChanged({ isOutbound: false })
                    }}
                    disabled={isTruckAppointment || !!store.selectedOrder || !!visitData}
                  />
                  <Chip
                    label={t('outbound', 'Outbound')}
                    color='primary'
                    sx={{ padding: '0 1rem' }}
                    variant={value === CarrierVisitDirection.Outbound ? undefined : 'outlined'}
                    onClick={() => {
                      onChange(CarrierVisitDirection.Outbound)
                      setDirection(CarrierVisitDirection.Outbound)
                      resetTransportFields()
                      onValidationParamsChanged({ isOutbound: true })
                    }}
                    disabled={isTruckAppointment || !!store.selectedOrder || !!visitData}
                  />
                </Box>
              )}
            />
          }
        />
        <Controller
          control={control}
          name={`transportAssigned`}
          render={({ field: { value, onChange } }) => (
            <Paper variant='outlined' sx={{ p: '1rem' }}>
              <Box display='flex' alignItems={'center'} justifyContent='space-between'>
                <Typography
                  variant='subtitle2'
                  color={isOrderFulfilled || !!visitData ? theme.palette.text.disabled : ''}
                >
                  {t('transportAssigned', 'Transport assigned')}?
                </Typography>
                <YesNoChipBox
                  disableYes={isTruckAppointment || isOrderFulfilled || !!visitData}
                  disableNo={isTruckAppointment || isOrderFulfilled || !!visitData}
                  activeOption={value ? 'Yes' : 'No'}
                  onClickYes={() => {
                    onChange(true)
                  }}
                  onClickNo={() => {
                    resetTransportFields()
                    onChange(false)
                  }}
                />
              </Box>
              {value && (
                <Grid sx={{ mt: 1.5 }} container spacing={2}>
                  {direction === CarrierVisitDirection.Inbound && (
                    <>
                      <Grid
                        size={{
                          sm: 4,
                          xs: 12,
                        }}
                      >
                        <Controller
                          control={control}
                          name={`carrierVisitType`}
                          rules={{ required: true }}
                          render={({ field: { onChange, value } }) => (
                            <FormControl fullWidth error={!!errors.carrierVisitType}>
                              <InputLabel>{t('inbound', 'Inbound')}</InputLabel>
                              <Select
                                defaultValue=''
                                value={value ?? ''}
                                label={t('inbound', 'Inbound')}
                                disabled={
                                  isTruckAppointment ||
                                  isOrderFulfilled ||
                                  !!visitData ||
                                  disableCarrierVisitSelection
                                }
                                onChange={(e: any) => {
                                  onChange(e.target.value)
                                  setCarrierType(e.target.value)
                                  setCarrierVisit(undefined)
                                  setValue('carrierVisit', undefined)
                                  setValue('railTrackId', undefined)
                                  setValue('railcarId', undefined)
                                }}
                              >
                                <MenuItem value='Train'>{t('train', 'Train')}</MenuItem>
                                <MenuItem value='Truck'>{t('truck', 'Truck')}</MenuItem>
                                <MenuItem value='Vessel'>{t('vessel', 'Vessel')}</MenuItem>
                              </Select>
                              {errors.carrierVisitType && (
                                <FormHelperText>
                                  {t('fieldIsRequired', 'Field is required.')}
                                </FormHelperText>
                              )}
                            </FormControl>
                          )}
                        />
                      </Grid>

                      <Grid
                        size={{
                          sm: carrierType === 'Train' ? 4 : 8,
                          xs: 12,
                        }}
                      >
                        <RenderDirectionExtraField
                          error={errors.carrierVisitType}
                          carrierVisit={carrierVisit}
                          control={control}
                          disabled={isOrderFulfilled || !!visitData || disableCarrierVisitSelection}
                          carrierType={carrierType}
                          onCarrierVisitChange={visit => {
                            setCarrierVisit(visit)
                            setValue('railTrackId', undefined)
                            setValue('railcarId', undefined)
                          }}
                        />
                      </Grid>

                      {carrierType === 'Train' && (
                        <>
                          <Grid
                            size={{
                              sm: 2,
                              xs: 12,
                            }}
                          >
                            <RailTrackControlledAutocomplete
                              control={control}
                              errors={errors}
                              setValue={setValue}
                              name='railTrackId'
                              label={t('railTrack', 'Rail Track')}
                              options={railTracks}
                            />
                          </Grid>
                          <Grid
                            size={{
                              sm: 2,
                              xs: 12,
                            }}
                          >
                            <RailcarControlledAutocomplete
                              control={control}
                              errors={errors}
                              name='railcarId'
                              label={t('railcarNo', 'Railcar no.')}
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                  {direction === CarrierVisitDirection.Outbound && (
                    <>
                      <Grid
                        size={{
                          sm: 4,
                          xs: 12,
                        }}
                      >
                        <Controller
                          control={control}
                          name={`carrierVisitType`}
                          rules={{ required: true }}
                          render={({ field: { onChange, value } }) => (
                            <FormControl fullWidth error={!!errors.carrierVisitType}>
                              <InputLabel>{t('outbound', 'Outbound')}</InputLabel>
                              <Select
                                defaultValue=''
                                value={value ?? ''}
                                label={t('outbound', 'Outbound')}
                                disabled={
                                  isTruckAppointment ||
                                  isOrderFulfilled ||
                                  !!visitData ||
                                  disableCarrierVisitSelection
                                }
                                onChange={(e: any) => {
                                  onChange(e.target.value)
                                  setCarrierType(e.target.value)
                                  setCarrierVisit(undefined)
                                  setValue('carrierVisit', undefined)
                                  setValue('railTrackId', undefined)
                                  setValue('railcarId', undefined)
                                }}
                              >
                                <MenuItem value='Train'>{t('train', 'Train')}</MenuItem>
                                <MenuItem value='Truck'>{t('truck', 'Truck')}</MenuItem>
                                <MenuItem value='Vessel'>{t('vessel', 'Vessel')}</MenuItem>
                              </Select>
                              {errors.carrierVisitType ? (
                                <FormHelperText>
                                  {t('fieldIsRequired', 'Field is required.')}
                                </FormHelperText>
                              ) : (
                                ''
                              )}
                            </FormControl>
                          )}
                        />
                      </Grid>

                      <Grid
                        size={{
                          sm: carrierType === 'Train' ? 4 : 8,
                          xs: 12,
                        }}
                      >
                        <RenderDirectionExtraField
                          error={errors.carrierVisitType}
                          carrierVisit={carrierVisit}
                          control={control}
                          disabled={isOrderFulfilled || !!visitData || disableCarrierVisitSelection}
                          carrierType={carrierType}
                          onCarrierVisitChange={visit => {
                            setCarrierVisit(visit)
                            setValue('railTrackId', undefined)
                            setValue('railcarId', undefined)
                          }}
                        />
                      </Grid>

                      {carrierType === 'Train' && (
                        <>
                          <Grid
                            size={{
                              sm: 2,
                              xs: 12,
                            }}
                          >
                            <RailTrackControlledAutocomplete
                              control={control}
                              errors={errors}
                              setValue={setValue}
                              name='railTrackId'
                              label={t('railTrack', 'Rail Track')}
                              options={railTracks}
                            />
                          </Grid>
                          <Grid
                            size={{
                              sm: 2,
                              xs: 12,
                            }}
                          >
                            <RailcarControlledAutocomplete
                              control={control}
                              errors={errors}
                              name='railcarId'
                              label={t('railcarNo', 'Railcar no.')}
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              )}
            </Paper>
          )}
        />
      </Stack>
    )
  },
)
